import { Controller } from "@hotwired/stimulus"
import loader from '@monaco-editor/loader'

/**
 * Get the theme that should be used for the editor. Uses app theme (standoutdark)
 * when set, otherwise falls back to system preference.
 */
const getEditorTheme = () => {
  if (document.documentElement?.dataset?.theme === 'standoutdark') {
    return 'vs-dark'
  }

  return 'vs'
}

/**
 * List allowed themes
 */
const allowedThemes = new Set(['vs', 'vs-dark'])

const updateAllEditorsTheme = async () => {
  try {
    const monaco = await loader.init()
    const theme = getEditorTheme()
    monaco.editor.getEditors().forEach((ed) => {
      ed.updateOptions({ theme: allowedThemes.has(theme) ? theme : 'vs' })
    })
  } catch (_e) {
    // Monaco not loaded yet
  }
}

window.addEventListener("themechange", updateAllEditorsTheme)
window
  .matchMedia("(prefers-color-scheme: dark)")
  .addEventListener("change", updateAllEditorsTheme)

export default class extends Controller {
  static targets = [
    'input', // Optional
    'toggle',
    'toggleLabel',
    'container'
  ]
  static values = {
    language: String,   // Required
    readOnly: Boolean,  // Optional
    minimap: Boolean,   // Optional
    encodedBody: String, // Optional
    originalValue: String // Optional - the original value for diff
  }

  /**
   * The code body. Will try to fetch from input target. If input target is not
   * defined it will use the inner text for the controller container instead.
   */
  getCodeBody() {
    if (this.hasInputTarget) { return this.inputTarget.value }
    if (this.hasEncodedBodyValue) {
      return Buffer.from(this.encodedBodyValue, 'base64').toString('utf8')
    }
    return ''
  }

  /**
   * If the editor should be initialized as a read only editor
   */
  getReadOnly() {
    if (this.hasReadOnlyValue) {
      return this.readOnlyValue
    }

    return false
  }

  /**
   * If the editor should be initialized with a minimap
   */
  useMinimap() {
    if (this.hasMinimapValue) {
      return this.minimapValue
    }

    return false
  }

  /**
   * Called anytime the controller is connected to the DOM
   */
  async connect() {
    this.updateHeight = this.updateHeight.bind(this)
    this.handleGlobalClick = this.handleGlobalClick.bind(this)
    this.originalValue = this.getCodeBody()
    let monaco = await loader.init()

    if (this.hasOriginalValueValue && this.shouldShowDiff()) {
      this.createDiffEditor(monaco)
    } else {
      this.createRegularEditor(monaco)
    }

    this.updateHeight()
    this.setupUpdateFrameListeners()
    this.setupGlobalClickListener()
  }

  /**
   * Create a regular Monaco editor
   */
  createRegularEditor(monaco) {
    this.editor = monaco.editor.create(this.containerTarget, {
      value: this.getCodeBody(),
      language: this.languageValue,
      scrollBeyondLastLine: false,
      wordWrap: 'on',
      wrappingStrategy: 'advanced',
      theme: getEditorTheme(),
      readOnly: this.getReadOnly(),
      lineNumbers: 'on',
      fontSize: 14,
      automaticLayout: true,
      scrollbar: {
        vertical: 'hidden',
        verticalScrollbarSize: 0,
        alwaysConsumeMouseWheel: false
      },
      minimap: {
        enabled: this.useMinimap()
      }
    });

    this.setupEditorListeners()
  }

  /**
   * Create a diff editor using Monaco's built-in diff functionality
   */
  createDiffEditor(monaco) {
    const originalModel = monaco.editor.createModel(
      this.originalValueValue || '',
      this.languageValue
    );

    const modifiedModel = monaco.editor.createModel(
      this.getCodeBody(),
      this.languageValue
    );

    this.editor = monaco.editor.createDiffEditor(this.containerTarget, {
      theme: getEditorTheme(),
      automaticLayout: true,
      readOnly: this.readOnlyValue,
      renderSideBySide: true,
      scrollBeyondLastLine: false,
      wordWrap: "on",
      wrappingStrategy: "advanced",
      lineNumbers: "on",
      fontSize: 14,
      originalEditable: false,
      renderIndicators: true,
      renderMarginRevertIcon: false,
      renderOverviewRuler: true,
      renderChanges: false,
      diffAlgorithm: "advanced",
      ignoreTrimWhitespace: false,
      scrollbar: {
        useShadows: false,
        vertical: "auto",
        horizontal: "auto",
        alwaysConsumeMouseWheel: false
      },
      minimap: { enabled: false }
    })

    this.editor.setModel({
      original: originalModel,
      modified: modifiedModel
    });

    this.setupDiffEditorListeners()
  }

  /**
   * Setup listeners for regular editor
   */
  setupEditorListeners() {
    this.editor.getModel().onDidChangeContent(() => {
      const value = this.editor.getModel().getValue()
      if (this.hasInputTarget) { this.inputTarget.value = value }
      this.updateHeight()
    })
  }

  /**
   * Setup listeners for diff editor
   */
  setupDiffEditorListeners() {
    this.editor.getModifiedEditor().getModel().onDidChangeContent(() => {
      const value = this.editor.getModifiedEditor().getModel().getValue()
      if (this.hasInputTarget) { this.inputTarget.value = value }
      this.updateHeight()
    })
  }

  /**
   * Setup global click listener to submit form when clicking outside editor
   */
  setupGlobalClickListener() {
    document.addEventListener('click', this.handleGlobalClick, true)
  }

  /**
   * Handle global click events - submit form if clicking outside editor and value changed
   */
  handleGlobalClick(event) {
    if (!this.editor) return
    // Check if click is inside the editor container (covers both regular and diff editor views)
    const isInContainer = this.containerTarget.contains(event.target)
    // Check if click is on the toggle or its label (don't submit when toggling)
    const isOnToggle = (this.hasToggleTarget && (
      event.target === this.toggleTarget ||
      this.toggleTarget.contains(event.target)
    )) || (this.hasToggleLabelTarget && (
      event.target === this.toggleLabelTarget ||
      this.toggleLabelTarget.contains(event.target)
    ))
    // If click is inside editor container or on toggle, don't submit
    if (isInContainer || isOnToggle) return
    // Get current value and check if it has changed from original
    const currentValue = this.getCurrentValue()
    if (currentValue !== this.originalValue) {
      this.submitForm()
    }
  }

  /**
   * Check if diff should be shown based on toggle state
   */
  shouldShowDiff() {
    return this.hasToggleTarget && this.toggleTarget.checked
  }

  /**
   * Toggle between diff and regular editor
   */
  async toggleDiff() {
    if (!this.editor) return

    const monaco = await loader.init()

    // Dispose current editor
    this.editor.dispose()

    if (this.shouldShowDiff()) {
      this.createDiffEditor(monaco)
    } else {
      this.createRegularEditor(monaco)
    }

    this.updateHeight()
  }

  /**
   * Get current value from either editor type
   */
  getCurrentValue() {
    if (this.editor.getModifiedEditor) {
      // Diff editor
      return this.editor.getModifiedEditor().getModel().getValue()
    } else {
      // Regular editor
      return this.editor.getModel().getValue()
    }
  }

  updateHeight() {
    window.requestAnimationFrame(() => {
      if (!this || !this.editor) return

      const isDiff = typeof this.editor.getModifiedEditor === 'function'
      const contentEditor = isDiff ? this.editor.getModifiedEditor() : this.editor
      if (!contentEditor) return

      const contentHeight = Math.min(1000, contentEditor.getContentHeight())

      const host = this.containerTarget
      const cssWidth = parseFloat(getComputedStyle(host).width) || host.clientWidth || 0

      host.style.height = `${contentHeight}px`
      this.editor.layout({ width: cssWidth, height: contentHeight })
    })
  }

  /**
   * Called anytime the controller is disconnected from the DOM
   */
  disconnect() {
    this.teardownUpdateFrameListeners()
    document.removeEventListener('click', this.handleGlobalClick, true)
    this.editor && this.editor.dispose()
  }

  /**
   * Add listeners and events that should be triggered to update the frame size
   * of the editor.
   */
  setupUpdateFrameListeners() {
    const isDiff = typeof this.editor.getModifiedEditor === 'function'

    if (isDiff) {
      const orig = this.editor.getOriginalEditor()
      const mod = this.editor.getModifiedEditor()
      orig.onDidContentSizeChange(this.updateHeight)
      mod.onDidContentSizeChange(this.updateHeight)
    } else {
      this.editor.onDidContentSizeChange(this.updateHeight)
    }

    document.addEventListener('visibilitychange', this.updateHeight)
    this.resizeObserver = new ResizeObserver(() => this.updateHeight())
    this.resizeObserver.observe(this.containerTarget)
  }

  /**
   * Remove listeners and events that is triggered to update the frame size of
   * the editor.
   */
  teardownUpdateFrameListeners() {
    document.removeEventListener('visibilitychange', this.updateHeight, false)
    this.resizeObserver && this.resizeObserver.disconnect()
  }

  /**
   * Submit the form when editor loses focus
   * Only submits if the form has the auto-submit controller
   */
  submitForm() {
    if (this.hasInputTarget) {
      const form = this.inputTarget.closest('form')
      if (form) {
        // Check if form has auto-submit controller
        const controllerAttr = form.getAttribute('data-controller')
        if (controllerAttr && controllerAttr.includes('auto-submit')) {
          form.requestSubmit()
        }
      }
    }
  }
}
